import { capitalize } from 'lodash'
import React, { isValidElement, useEffect, useState, type ReactNode } from 'react'
import { type ChipColor, taskDownload } from '../TaskFeature'
import { Box, Chip } from '@mui/material'
import theme from '../../theme'
import { useQuery } from '@tanstack/react-query'
import { green, red } from '../../colors'
import { defaultUploadStatus } from './WizardPages/UploadPeopleAndCoursesPage'
import { getTaskById } from '../../APIManager/TaskManager'
import { defaultCSVUploadDescription, StatusLabels, wizardKeys } from './constants'
import { TaskResultStatus } from './styles'

const key = wizardKeys.localKey
const setupSurveyTemplatePageKey = wizardKeys.SetupSurveyTemplatePage
const setupTermPageKey = wizardKeys.SetupTermPage

const errorStatusDescription = (taskIds: Record<string, string>, type: string): ReactNode => {
  return (
    <Box sx = {{ color: red[800] }}>
      There was an error uploading the CSV. Click{' '}
      <span
        style={{ cursor: 'pointer', color: theme.palette.primary.main, textDecoration: 'underline' }}
        onClick={() => { void taskDownload(taskIds[type]) }}
      >
        here
      </span>
      {' '} to download the results.
    </Box>
  )
}

const successStatusDescription = (taskIds: Record<string, string>, type: string): ReactNode => {
  return (
    <Box sx= {{ color: green[700] }}>
      CSV uploaded successfully. Click{' '}
      <span
        style={{ cursor: 'pointer', color: theme.palette.primary.main, textDecoration: 'underline' }}
        onClick={() => { void taskDownload(taskIds[type]) }}
      >
        here
      </span>
      {' '} to download the results.
    </Box>
  )
}

const pendingStatusDescription = (loadingDotCount: number): string => {
  return `Pending${'.'.repeat(loadingDotCount)}`
}

// handling for updating the status and description of the upload state
export const handleUpdateStatus = (
  type: string,
  status: string,
  setUploadStatuses: React.Dispatch<React.SetStateAction<Record<string, ReactNode>>>,
  setDescriptions: React.Dispatch<React.SetStateAction<Record<string, ReactNode>>>,
  taskIds: Record<string, string>,
  loadingDotCount: number
): void => {
  let color: ChipColor = 'primary'
  let descriptionText: ReactNode
  const statusText = capitalize(status.toLowerCase())
  switch (status) {
    case TaskResultStatus.ERROR:
      color = 'error'
      descriptionText = errorStatusDescription(taskIds, type)
      break
    case TaskResultStatus.SUCCESSFUL:
      color = 'success'
      descriptionText = successStatusDescription(taskIds, type)
      break
    case TaskResultStatus.PENDING:
      color = 'warning'
      descriptionText = pendingStatusDescription(loadingDotCount)
      break
  }
  setUploadStatuses((prevStatuses) => ({
    ...prevStatuses,
    [type]: <Chip label={statusText} color={color} variant='outlined' size='small'/>
  }))
  setDescriptions((prevDescriptions) => ({
    ...prevDescriptions,
    [type]: descriptionText
  }))
}

// checks if there is a pending status in the upload statuses
export const pendingExistsInStatus = (uploadStatuses: Record<string, ReactNode>): boolean => {
  return Object.values(uploadStatuses).some((chip) => {
    return isValidElement(chip) &&
      typeof chip.props.label === 'string' &&
      chip.props.label.toLowerCase() === 'pending'
  })
}

export const useLoadingDots = (): number => {
  const [dotCount, setDotCount] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setDotCount(prev => (prev + 1) % 4)
    }, 3000)
    return () => { clearInterval(interval) }
  }, [])

  return dotCount
}

export const updatePendingDescription = (
  uploadStatuses: Record<string, ReactNode>,
  setDescriptions: React.Dispatch<React.SetStateAction<Record<string, ReactNode>>>,
  loadingDotCount: number,
  options: string[]
): void => {
  setDescriptions(prev => {
    const updated = { ...prev }
    Object.entries(uploadStatuses).forEach(([type, chip]) => {
      let label = ''
      if (React.isValidElement(chip) && typeof chip.props.label === 'string') {
        label = chip.props.label.toLowerCase()
      }
      if (label === 'pending' && options.includes(type)) {
        updated[type] = `Pending${'.'.repeat(loadingDotCount)}`
      }
    })
    return updated
  })
}

export const useTaskStatusQuery = (
  taskIds: Record<string, string>,
  uploadStatuses: Record<string, ReactNode>,
  setUploadStatuses: React.Dispatch<React.SetStateAction<Record<string, ReactNode>>>,
  setDescriptions: React.Dispatch<React.SetStateAction<Record<string, ReactNode>>>,
  loadingDotCount: number
): ReturnType<typeof useQuery> => {
  return useQuery({
    queryKey: ['tasks', taskIds],
    queryFn: async (): Promise<boolean> => {
      await Promise.all(
        Object.entries(taskIds).map(async ([type, id]) => {
          if (id) {
            await getTaskById(id)
              .then((task) => {
                handleUpdateStatus(type, task.data.status, setUploadStatuses, setDescriptions, taskIds, loadingDotCount)
              })
          }
        })
      )
      return false
    },
    refetchInterval: () => {
      const statuses = Object.values(uploadStatuses)
      return statuses.some(status => {
        let label = ''
        if (isValidElement(status) && typeof status.props.label === 'string') {
          label = status.props.label.toLowerCase()
        }
        return label === 'pending' || label === 'error'
      })
        ? 15000
        : false
    }
  })
}

export const getLocallyStoredData = (): Record<string, any> => {
  let savedData = {}
  try {
    savedData = JSON.parse(localStorage.getItem(key) ?? '{}')
    return savedData
  } catch (error) {
    console.error('Error parsing local storage data:', error)
    return {}
  }
}

export const getLocallyStoredCurrentPage = (): number => {
  const savedData = getLocallyStoredData()
  if (savedData?.currPage) {
    return savedData.currPage
  }
  return 0
}

export const getLocallyStoredTaskIdsByPage = (page: string, options: string[]): Record<string, string> => {
  const savedData = getLocallyStoredData()
  if (savedData?.[page]?.taskIds) {
    return savedData[page].taskIds
  }
  return Object.fromEntries(options.map((option: string) => [option, '']))
}

export const getLocallyStoredUploadStatusesByPage = (page: string, options: string[]): Record<string, ReactNode> => {
  const savedData = getLocallyStoredData()
  // return defaults if local save doesn't exist
  if (!savedData?.[page]?.uploadStatuses) {
    return Object.fromEntries(options.map((option: string) => [option, defaultUploadStatus]))
  }

  const uploadProps: Record<string, { label: string, color: ChipColor }> = savedData[page].uploadStatuses
  const uploadStatuses: Record<string, ReactNode> = {}
  Object.entries(uploadProps).forEach(([type, status]) => {
    uploadStatuses[type] = <Chip label={status.label} color={status.color} variant='outlined' size='small'/>
  })
  return uploadStatuses
}

export const getLocallyStoredDescriptionsByPage = (page: string, taskIds: Record<string, string>, loadingDotCount: number, options: string[]): Record<string, ReactNode> => {
  const savedData = getLocallyStoredData()
  if (!savedData?.[page]?.uploadStatuses) {
    return Object.fromEntries(options.map((option: string) => [option, defaultCSVUploadDescription]))
  }

  const uploadProps: Record<string, { label: string, color: ChipColor }> = savedData[page].uploadStatuses
  const descriptions: Record<string, ReactNode> = {}
  Object.entries(uploadProps).forEach(([type, status]) => {
    switch (status.label) {
      case StatusLabels.Pending:
        descriptions[type] = pendingStatusDescription(loadingDotCount)
        break
      case StatusLabels.Success:
        descriptions[type] = successStatusDescription(taskIds, type)
        break
      case StatusLabels.Error:
        descriptions[type] = errorStatusDescription(taskIds, type)
        break
      default:
        descriptions[type] = defaultCSVUploadDescription
    }
  })
  return descriptions
}

export const getLocallyStoredTermId = (): string | undefined => {
  const savedData = getLocallyStoredData()
  if (savedData?.[setupTermPageKey]?.termId) {
    return savedData[setupTermPageKey].termId
  }
  return undefined
}

export const getLocallyStoredTermName = (page: string): string | undefined => {
  const savedData = getLocallyStoredData()
  if (savedData?.[page]?.termName) {
    return savedData[page].termName
  }
  return undefined
}

export const getLocallyStoredDistributionSeriesId = (): string | undefined => {
  const savedData = getLocallyStoredData()
  if (savedData?.[setupSurveyTemplatePageKey]?.distributionSeriesId) {
    return savedData[setupSurveyTemplatePageKey].distributionSeriesId
  }
  return undefined
}

export const setLocallyStoredTaskIdsByPage = (page: string, taskIds: Record<string, string>): void => {
  const savedData = getLocallyStoredData()
  const updatedData = {
    ...savedData,
    [page]: {
      ...savedData[page],
      taskIds
    }
  }
  localStorage.setItem(key, JSON.stringify(updatedData))
}

export const setLocallyStoredCurrentPage = (page: number): void => {
  const savedData = getLocallyStoredData()
  const updatedData = {
    ...savedData,
    currPage: page
  }
  localStorage.setItem(key, JSON.stringify(updatedData))
}

export const setLocallyStoredUploadStatusesAndDescriptionsByPage = (page: string, uploadStatuses: Record<string, ReactNode>): void => {
  const savedData = getLocallyStoredData()
  const updatedUploadStatuses = Object.fromEntries(
    Object.entries(uploadStatuses).map(([type, status]) => {
      if (isValidElement(status)) {
        const { label, color } = status.props as { label: string, color: ChipColor }
        return [type, { label, color }]
      }
      return [type, { label: String(status), color: 'primary' }]
    })
  )
  const updatedData = {
    ...savedData,
    [page]: {
      ...savedData[page],
      uploadStatuses: updatedUploadStatuses
    }
  }
  localStorage.setItem(key, JSON.stringify(updatedData))
}

export const setLocallyStoredTermIdAndName = (termId: string | undefined, termName: string | undefined): void => {
  const savedData = getLocallyStoredData()
  const updatedData = {
    ...savedData,
    [setupTermPageKey]: {
      ...savedData[setupTermPageKey],
      termId,
      termName
    }
  }
  localStorage.setItem(key, JSON.stringify(updatedData))
}

export const setLocallyStoredDistributionSeriesId = (distributionSeriesId: string | undefined): void => {
  const savedData = getLocallyStoredData()
  const updatedData = {
    ...savedData,
    [setupSurveyTemplatePageKey]: {
      ...savedData[setupSurveyTemplatePageKey],
      distributionSeriesId
    }
  }
  localStorage.setItem(key, JSON.stringify(updatedData))
}

export const setLocallyStoredIsLastPageSeeable = (isLastPageSeeable: boolean): void => {
  const savedData = getLocallyStoredData()
  const updatedData = {
    ...savedData,
    isLastPageSeeable
  }
  localStorage.setItem(key, JSON.stringify(updatedData))
}

export const deleteLocallyStoredFieldInSetupTourData = (fieldName: string): void => {
  const savedData = getLocallyStoredData()
  const { [fieldName]: _, ...rest } = savedData
  localStorage.setItem(key, JSON.stringify(rest))
}

export const setLocallyStoredNewlyAddedTerms = (newlyAddedTerms: string[]): void => {
  const savedData = getLocallyStoredData()
  const updatedData = {
    ...savedData,
    [setupSurveyTemplatePageKey]: {
      ...savedData[setupSurveyTemplatePageKey],
      newlyAddedTerms
    }
  }
  localStorage.setItem(key, JSON.stringify(updatedData))
}

export const getLocallyStoredNewlyAddedTerms = (): string[] => {
  const savedData = getLocallyStoredData()
  if (savedData?.[setupSurveyTemplatePageKey]?.newlyAddedTerms) {
    return savedData[setupSurveyTemplatePageKey].newlyAddedTerms
  }
  return []
}

export const deleteLocallyStoredWizardData = (): void => {
  localStorage.removeItem(key)
}
